/*
  TeamDevelopment page — Phase 1 mentoring hub mounted at
  /learning/team-development.

  Three sub-areas:
    * "Team" view — scoped staff list with mentor + progress rollups
      (Owner/Manager/Mentor). Clicking a row opens the Staff Profile.
    * Staff Profile — tabs for Overview / Goals / Meetings.
    * Meeting editor — a lightweight dialog for the mentor to draft,
      the staff member to prep, and the mentor to publish.
*/
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Target, Users, CalendarClock, PlusCircle, ChevronLeft, Loader2,
  CheckCircle2, Clock, Archive, UserPlus, Save, Send, FileText,
  AlertTriangle, Lock, Shield,
} from "lucide-react";
import {
  PrivateNotes, AuditTrailDialog, MentorAssignmentDialog,
} from "@/components/team-development/Phase3";
import TrainingProgress from "@/components/team-development/TrainingProgress";

const CATEGORY_LABELS = {
  clinical_reasoning: "Clinical reasoning",
  exercise_prescription: "Exercise prescription",
  client_communication: "Client communication",
  documentation: "Documentation",
  program_delivery: "Program delivery",
  operational_skills: "Operational skills",
  professional_development: "Professional development",
  other: "Other",
};

const GOAL_STATUS_STYLES = {
  draft: "bg-zinc-100 text-zinc-700 border-zinc-200",
  active: "bg-indigo-100 text-indigo-800 border-indigo-200",
  paused: "bg-amber-100 text-amber-800 border-amber-200",
  achieved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  closed: "bg-zinc-100 text-zinc-600 border-zinc-200",
};

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

// ---------------------------------------------------------------------------
// Team view
// ---------------------------------------------------------------------------
function TeamList({ onPick, me, onManageMentors, onStaffLoaded }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const canManage = ["owner", "manager"].includes((me?.role || "").toLowerCase());
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/team-development/staff");
      setStaff(r.data.staff || []);
      onStaffLoaded?.(r.data.staff || []);
    } catch (e) {
      toast.error("Couldn't load team", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }, [onStaffLoaded]);
  useEffect(() => { load(); }, [load]);
  if (loading) {
    return (
      <div className="text-xs text-zinc-500 flex items-center gap-2 py-6">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading team…
      </div>
    );
  }
  if (staff.length === 0) {
    return <div className="text-xs text-zinc-500 py-6">No team members in your scope yet.</div>;
  }
  return (
    <Card className="rounded-lg border border-zinc-200 shadow-none overflow-hidden" data-testid="td-team-list">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200 text-left">
              <th className="px-3 py-2 font-medium text-zinc-700">Staff</th>
              <th className="px-3 py-2 font-medium text-zinc-700">Mentors</th>
              <th className="px-3 py-2 font-medium text-zinc-700">Active goals</th>
              <th className="px-3 py-2 font-medium text-zinc-700">Achieved</th>
              <th className="px-3 py-2 font-medium text-zinc-700">Overdue review</th>
              <th className="px-3 py-2 font-medium text-zinc-700">Next meeting</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {staff.map((s) => (
              <tr key={s.staff_id} className="border-b border-zinc-100 hover:bg-zinc-50/50" data-testid={`td-row-${s.staff_id}`}>
                <td className="px-3 py-2">
                  <div className="font-medium text-zinc-900">{s.name}</div>
                  <div className="text-2xs text-zinc-500 capitalize">{s.role}</div>
                </td>
                <td className="px-3 py-2">
                  {s.mentors.length === 0
                    ? <span className="text-2xs text-zinc-400 italic">no mentor</span>
                    : s.mentors.map((m) => (
                      <Badge key={m.user_id} className="bg-indigo-50 text-indigo-800 border-indigo-200 mr-1 text-2xs">
                        {m.name || m.user_id.slice(-6)}
                      </Badge>
                    ))}
                </td>
                <td className="px-3 py-2">{s.goal_counts?.active ?? 0}</td>
                <td className="px-3 py-2 text-emerald-700">{s.goal_counts?.achieved ?? 0}</td>
                <td className="px-3 py-2">
                  {(s.goal_counts?.overdue_review ?? 0) > 0
                    ? <Badge className="bg-red-100 text-red-800 border-red-200 text-2xs"><AlertTriangle className="h-2.5 w-2.5 mr-0.5" />{s.goal_counts.overdue_review}</Badge>
                    : <span className="text-zinc-300">—</span>}
                </td>
                <td className="px-3 py-2 text-2xs">{fmtDateTime(s.next_meeting_at)}</td>
                <td className="px-3 py-2 text-right">
                  {canManage && (
                    <Button
                      variant="outline" size="sm" className="text-2xs h-6 mr-1"
                      onClick={() => onManageMentors(s)}
                      data-testid={`td-manage-mentors-${s.staff_id}`}
                    >
                      <UserPlus className="h-3 w-3 mr-0.5" />Mentors
                    </Button>
                  )}
                  <Button variant="outline" size="sm" className="text-2xs h-6" onClick={() => onPick(s)} data-testid={`td-open-${s.staff_id}`}>
                    Open
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Goal editor dialog
// ---------------------------------------------------------------------------
function GoalDialog({ open, onClose, onSaved, staffId, categories, goal }) {
  const isEdit = Boolean(goal);
  const [form, setForm] = useState({
    title: goal?.title || "",
    description: goal?.description || "",
    category: goal?.category || "",
    success_criteria: goal?.success_criteria || "",
    target_date: goal?.target_date || "",
  });
  const [saving, setSaving] = useState(false);
  async function save() {
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/team-development/goals/${goal.id}`, form);
      } else {
        await api.post("/team-development/goals", { ...form, staff_id: staffId });
      }
      toast.success(isEdit ? "Goal updated" : "Goal created");
      onSaved();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit goal" : "New development goal"}</DialogTitle>
          <DialogDescription className="text-2xs">
            Draft → Active → Achieved/Closed. Closing requires a reason.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-2xs">Title *</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="td-goal-title" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-2xs">Category</Label>
              <Select value={form.category || "none"} onValueChange={(v) => setForm({ ...form, category: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="td-goal-category"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {categories.map((c) => <SelectItem key={c} value={c}>{CATEGORY_LABELS[c] || c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-2xs">Target date</Label>
              <Input type="date" value={form.target_date || ""} onChange={(e) => setForm({ ...form, target_date: e.target.value })} data-testid="td-goal-target" />
            </div>
          </div>
          <div>
            <Label className="text-2xs">Description</Label>
            <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="td-goal-description" />
          </div>
          <div>
            <Label className="text-2xs">Success criteria</Label>
            <Textarea rows={3} placeholder="How will we know this goal is met?" value={form.success_criteria} onChange={(e) => setForm({ ...form, success_criteria: e.target.value })} data-testid="td-goal-criteria" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="td-goal-save">
            {saving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Meeting editor dialog — covers prep / mentor edit / publish / amendment
// ---------------------------------------------------------------------------
const SECTION_KEYS = ["section_check_in", "section_reflection", "section_case", "section_feedback", "section_actions"];

function MeetingDialog({ open, onClose, onSaved, meetingId, template, profile, me, isWriter, isSelf }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({ summary: "", no_action_reason: "" });
  const [prep, setPrep] = useState({ success: "", challenge: "", priority: "", case_summary: "" });
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [amendmentReason, setAmendmentReason] = useState("");
  const [showAmend, setShowAmend] = useState(false);

  const load = useCallback(async () => {
    if (!meetingId) return;
    try {
      const r = await api.get(`/mentoring/meetings/${meetingId}`);
      setData(r.data);
      // Writers get the mentor draft; others may still see sections post-publish via r.data.revisions
      setDraft({ ...(r.data.draft || {}), summary: r.data.draft?.summary || "", no_action_reason: r.data.draft?.no_action_reason || "" });
      // Staff loads own prep; writer sees post-submit only
      if (r.data.preparation && typeof r.data.preparation.success === "string") {
        setPrep({
          success: r.data.preparation.success || "",
          challenge: r.data.preparation.challenge || "",
          priority: r.data.preparation.priority || "",
          case_summary: r.data.preparation.case_summary || "",
        });
      }
    } catch (e) {
      toast.error("Couldn't load meeting", { description: e?.response?.data?.detail || e.message });
    }
  }, [meetingId]);
  useEffect(() => { if (open) load(); }, [open, load]);

  async function saveDraft() {
    if (!isWriter) return;
    setSaving(true);
    try {
      await api.patch(`/mentoring/meetings/${meetingId}`, { draft });
      toast.success("Draft saved");
      load();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  async function publish() {
    const needsAmendment = (data?.published_revision || 0) >= 1;
    if (needsAmendment && !amendmentReason.trim()) {
      setShowAmend(true);
      return;
    }
    setPublishing(true);
    try {
      await api.post(
        `/mentoring/meetings/${meetingId}/publish`,
        { amendment_reason: amendmentReason || null },
        { headers: { "Idempotency-Key": `${meetingId}:${Date.now()}` } },
      );
      toast.success(needsAmendment ? "Amendment published" : "Meeting published");
      setShowAmend(false);
      setAmendmentReason("");
      load();
      onSaved?.();
    } catch (e) {
      toast.error("Publish failed", { description: e?.response?.data?.detail || e.message });
    } finally { setPublishing(false); }
  }

  async function savePrep() {
    setSaving(true);
    try {
      await api.put(`/mentoring/meetings/${meetingId}/preparation`, prep);
      toast.success("Preparation saved");
      load();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  async function submitPrep() {
    try {
      await api.post(`/mentoring/meetings/${meetingId}/preparation/submit`);
      toast.success("Preparation submitted — your mentor can now see it.");
      load();
    } catch (e) {
      toast.error("Submit failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function acknowledgeLatest(correction) {
    const rev = data.published_revision;
    try {
      await api.post(`/mentoring/meetings/${meetingId}/acknowledgements`, {
        revision_number: rev,
        correction_message: correction || null,
      });
      toast.success("Acknowledgement recorded");
      load();
    } catch (e) {
      toast.error("Ack failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  if (!data) {
    return (
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent><Loader2 className="h-5 w-5 animate-spin mx-auto" /></DialogContent>
      </Dialog>
    );
  }

  const latestRev = data.revisions?.find((r) => r.revision_number === data.published_revision);
  const myAck = data.acknowledgements?.find((a) => a.revision_number === data.published_revision && a.staff_id === data.staff_id);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Mentoring meeting · {fmtDateTime(data.scheduled_at)}
            {data.status === "published" && (
              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 ml-2 text-2xs">
                Published v{data.published_revision}
              </Badge>
            )}
            {data.status === "cancelled" && (
              <Badge className="bg-zinc-100 text-zinc-600 border-zinc-200 ml-2 text-2xs">Cancelled</Badge>
            )}
          </DialogTitle>
          <DialogDescription className="text-2xs">
            {data.duration_minutes} min · {data.location || "no location set"} · focus: {data.focus || "—"}
          </DialogDescription>
        </DialogHeader>

        {/* STAFF PREP SECTION */}
        {isSelf && (
          <Card className="p-3 rounded-md border-indigo-200 bg-indigo-50/40">
            <div className="text-xs font-medium text-indigo-900 flex items-center gap-1.5 mb-2">
              <FileText className="h-3.5 w-3.5" /> Your preparation
              {data.preparation?.submitted_at && (
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-2xs">Submitted</Badge>
              )}
            </div>
            <div className="space-y-2">
              <div>
                <Label className="text-2xs">One success this fortnight</Label>
                <Textarea rows={2} value={prep.success} onChange={(e) => setPrep({ ...prep, success: e.target.value })} data-testid="td-prep-success" />
              </div>
              <div>
                <Label className="text-2xs">One challenge</Label>
                <Textarea rows={2} value={prep.challenge} onChange={(e) => setPrep({ ...prep, challenge: e.target.value })} data-testid="td-prep-challenge" />
              </div>
              <div>
                <Label className="text-2xs">Development priority</Label>
                <Textarea rows={2} value={prep.priority} onChange={(e) => setPrep({ ...prep, priority: e.target.value })} data-testid="td-prep-priority" />
              </div>
              <div>
                <Label className="text-2xs">De-identified case (optional)</Label>
                <Textarea rows={3} placeholder="No client names or identifiers" value={prep.case_summary} onChange={(e) => setPrep({ ...prep, case_summary: e.target.value })} data-testid="td-prep-case" />
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={savePrep} disabled={saving} className="text-xs h-7" data-testid="td-prep-save"><Save className="h-3 w-3 mr-1" />Save</Button>
                {!data.preparation?.submitted_at && (
                  <Button onClick={submitPrep} className="text-xs h-7 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="td-prep-submit">
                    <Send className="h-3 w-3 mr-1" />Submit to mentor
                  </Button>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* WRITER DRAFT SECTION */}
        {isWriter && (
          <Card className="p-3 rounded-md border-zinc-200">
            <div className="text-xs font-medium text-zinc-900 mb-2">
              Meeting notes {data.status === "published" ? "(editing for amendment)" : "(draft)"}
            </div>
            {data.preparation?.success && (
              <div className="mb-3 text-2xs bg-indigo-50/40 border border-indigo-100 rounded px-2 py-1.5">
                <div className="font-medium text-indigo-900 mb-1">Staff preparation (submitted)</div>
                <div><b>Success:</b> {data.preparation.success}</div>
                <div><b>Challenge:</b> {data.preparation.challenge}</div>
                <div><b>Priority:</b> {data.preparation.priority}</div>
                {data.preparation.case_summary && <div><b>Case:</b> {data.preparation.case_summary}</div>}
              </div>
            )}
            <div className="space-y-2">
              {template?.sections?.map((s) => (
                <div key={s.key}>
                  <Label className="text-2xs">
                    {s.label} <span className="text-zinc-400 font-normal">· {s.minutes} min</span>
                  </Label>
                  <div className="text-2xs text-zinc-500 italic mb-1">{s.prompt}</div>
                  <Textarea
                    rows={2}
                    value={draft[s.key] || ""}
                    onChange={(e) => setDraft({ ...draft, [s.key]: e.target.value })}
                    data-testid={`td-draft-${s.key}`}
                  />
                </div>
              ))}
              <div className="border-t border-zinc-100 pt-2">
                <Label className="text-2xs">Shared summary * (required to publish)</Label>
                <Textarea rows={3} value={draft.summary} onChange={(e) => setDraft({ ...draft, summary: e.target.value })} data-testid="td-draft-summary" />
              </div>
              <div>
                <Label className="text-2xs">
                  "No actions required" reason (optional, if the Actions section is blank)
                </Label>
                <Input value={draft.no_action_reason || ""} onChange={(e) => setDraft({ ...draft, no_action_reason: e.target.value })} data-testid="td-draft-no-action" />
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={saveDraft} disabled={saving} className="text-xs h-7" data-testid="td-draft-save">
                  {saving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}Save draft
                </Button>
                {data.status !== "cancelled" && (
                  <Button onClick={publish} disabled={publishing} className="text-xs h-7 bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="td-meeting-publish">
                    {publishing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Send className="h-3 w-3 mr-1" />}
                    {data.published_revision >= 1 ? "Publish amendment" : "Publish meeting"}
                  </Button>
                )}
              </div>
              {showAmend && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2 mt-1">
                  <Label className="text-2xs">Why are you amending?</Label>
                  <Input value={amendmentReason} onChange={(e) => setAmendmentReason(e.target.value)} data-testid="td-meeting-amendment-reason" />
                  <div className="flex items-center gap-1 mt-1">
                    <Button variant="outline" size="sm" className="text-2xs h-6" onClick={() => { setShowAmend(false); setAmendmentReason(""); }}>Cancel</Button>
                    <Button size="sm" className="text-2xs h-6" onClick={publish}>Confirm amendment</Button>
                  </div>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* PUBLISHED SUMMARY (visible to everyone with access) */}
        {latestRev && (
          <Card className="p-3 rounded-md border-emerald-200 bg-emerald-50/30" data-testid="td-published-summary">
            <div className="text-xs font-medium text-emerald-900 flex items-center gap-1.5 mb-2">
              <CheckCircle2 className="h-3.5 w-3.5" /> Published v{latestRev.revision_number} · {fmtDateTime(latestRev.published_at)}
              {latestRev.amendment_reason && <span className="font-normal italic text-zinc-500">· {latestRev.amendment_reason}</span>}
            </div>
            <div className="whitespace-pre-wrap text-2xs mb-2">{latestRev.summary}</div>
            {SECTION_KEYS.map((k) => latestRev[k] ? (
              <div key={k} className="mt-1">
                <div className="text-2xs uppercase tracking-wider text-zinc-500">{(template?.sections || []).find((s) => s.key === k)?.label || k}</div>
                <div className="whitespace-pre-wrap text-2xs">{latestRev[k]}</div>
              </div>
            ) : null)}
            {isSelf && !myAck && (
              <div className="mt-3 flex items-center gap-2">
                <Button size="sm" variant="outline" className="text-2xs h-7" onClick={() => acknowledgeLatest()} data-testid="td-meeting-ack">
                  <CheckCircle2 className="h-3 w-3 mr-1" />Acknowledge
                </Button>
                <Button size="sm" variant="outline" className="text-2xs h-7" onClick={() => {
                  const msg = window.prompt("What correction do you want to request?");
                  if (msg) acknowledgeLatest(msg);
                }}>
                  Request correction
                </Button>
              </div>
            )}
            {myAck && (
              <div className="mt-2 text-2xs text-emerald-700">
                You acknowledged this on {fmtDateTime(myAck.acknowledged_at)}
                {myAck.correction_requested && <span className="text-amber-700 ml-1">(correction requested)</span>}
              </div>
            )}
          </Card>
        )}

        {/* PRIVATE NOTES — mentor side only, scoped to this meeting (Phase 3) */}
        {isWriter && !isSelf && (
          <PrivateNotes
            staffId={data.staff_id}
            scopeType="meeting"
            scopeId={meetingId}
            me={me}
            canWrite={true}
          />
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Staff Profile
// ---------------------------------------------------------------------------
function StaffProfile({ staffId, me, categories, template, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [showGoal, setShowGoal] = useState(false);
  const [editGoal, setEditGoal] = useState(null);
  const [showMeeting, setShowMeeting] = useState(false);
  const [showNewMeeting, setShowNewMeeting] = useState(false);
  const [activeMeetingId, setActiveMeetingId] = useState(null);
  const [showAudit, setShowAudit] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/team-development/staff/${staffId}`);
      setData(r.data);
    } catch (e) {
      toast.error("Couldn't load profile", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }, [staffId]);
  useEffect(() => { load(); }, [load]);

  async function reviewGoal(goal, status, reason) {
    try {
      await api.post(`/team-development/goals/${goal.id}/review`, { status, reason: reason || null });
      toast.success(`Goal marked ${status}`);
      load();
    } catch (e) {
      toast.error("Review failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  if (loading || !data) return <div className="text-xs text-zinc-500 py-6 flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" />Loading…</div>;

  const { staff, mentors, goals, meetings, is_writer, is_self } = data;
  const canManagerView = ["owner", "manager"].includes((me?.role || "").toLowerCase());
  // Private-note writer rights: mentor on this staff OR owner/manager (not staff itself).
  const canPrivateNote = is_writer && !is_self;

  return (
    <div className="space-y-3" data-testid="td-profile">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={onBack} className="text-xs h-7" data-testid="td-profile-back">
          <ChevronLeft className="h-3.5 w-3.5 mr-0.5" />Back to team
        </Button>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold text-zinc-950">{staff.name}</h2>
          <div className="text-2xs text-zinc-500 capitalize">
            {staff.role}
            {mentors.length > 0 && (
              <span className="ml-2">
                · mentored by {mentors.map((m) => m.mentor_name || m.mentor_id.slice(-6)).join(", ")}
              </span>
            )}
          </div>
        </div>
        {canManagerView && (
          <Button
            variant="outline" size="sm" className="text-xs h-7"
            onClick={() => setShowAudit(true)}
            data-testid="td-audit-open"
          >
            <Shield className="h-3 w-3 mr-1" />Audit trail
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="inline-flex rounded-md border border-zinc-200 bg-white p-0.5" data-testid="td-profile-tabs">
        {["overview", "goals", "meetings", "actions", "training", ...(canPrivateNote ? ["notes"] : [])].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`text-xs px-3 py-1 rounded capitalize ${tab === t ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}
            data-testid={`td-profile-tab-${t}`}
          >
            {t === "notes" ? (<span className="inline-flex items-center gap-1"><Lock className="h-2.5 w-2.5" />notes</span>) : t}
            {t === "goals" && goals.length > 0 && <span className="ml-1 text-2xs opacity-70">({goals.length})</span>}
            {t === "meetings" && meetings.length > 0 && <span className="ml-1 text-2xs opacity-70">({meetings.length})</span>}
          </button>
        ))}
      </div>

      {/* OVERVIEW */}
      {tab === "overview" && (
        <Card className="p-4 rounded-lg border border-zinc-200 shadow-none space-y-3">
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="border border-zinc-100 rounded p-2">
              <div className="text-2xs uppercase tracking-wider text-zinc-500">Active goals</div>
              <div className="text-xl font-semibold">{goals.filter((g) => g.status === "active").length}</div>
            </div>
            <div className="border border-zinc-100 rounded p-2">
              <div className="text-2xs uppercase tracking-wider text-zinc-500">Achieved</div>
              <div className="text-xl font-semibold text-emerald-700">{goals.filter((g) => g.status === "achieved").length}</div>
            </div>
            <div className="border border-zinc-100 rounded p-2">
              <div className="text-2xs uppercase tracking-wider text-zinc-500">Meetings on record</div>
              <div className="text-xl font-semibold">{meetings.length}</div>
            </div>
          </div>
          <div className="border-t border-zinc-100 pt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-2xs uppercase tracking-wider text-zinc-500">Training progress</div>
              <Button
                size="sm" variant="outline" className="text-2xs h-6"
                onClick={() => setTab("training")}
                data-testid="td-overview-training-open"
              >
                View full breakdown
              </Button>
            </div>
            <TrainingProgress staffId={staffId} me={me} canSign={is_writer} />
          </div>
        </Card>
      )}

      {/* GOALS */}
      {tab === "goals" && (
        <div className="space-y-2" data-testid="td-goals-list">
          {is_writer && (
            <Button size="sm" className="text-xs h-7 bg-zinc-950 hover:bg-zinc-800 text-white" onClick={() => { setEditGoal(null); setShowGoal(true); }} data-testid="td-new-goal-btn">
              <PlusCircle className="h-3 w-3 mr-1" />New goal
            </Button>
          )}
          {goals.length === 0 && <div className="text-2xs text-zinc-500">No goals yet.</div>}
          {goals.map((g) => (
            <Card key={g.id} className="p-3 rounded-md border border-zinc-200 shadow-none" data-testid={`td-goal-${g.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-zinc-950">{g.title}</div>
                  <div className="text-2xs text-zinc-500">
                    {g.category && (CATEGORY_LABELS[g.category] || g.category)}
                    {g.target_date && <span className="ml-1">· target {fmtDate(g.target_date)}</span>}
                  </div>
                  {g.description && <div className="text-2xs mt-1 whitespace-pre-wrap">{g.description}</div>}
                  {g.success_criteria && (
                    <div className="text-2xs mt-1 bg-zinc-50 border border-zinc-100 rounded p-1.5">
                      <span className="text-2xs uppercase tracking-wider text-zinc-500">Success criteria</span>
                      <div className="whitespace-pre-wrap">{g.success_criteria}</div>
                    </div>
                  )}
                </div>
                <Badge className={`text-2xs flex-shrink-0 ${GOAL_STATUS_STYLES[g.status]}`}>{g.status}</Badge>
              </div>
              {is_writer && (
                <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                  <Button variant="outline" size="sm" className="text-2xs h-6" onClick={() => { setEditGoal(g); setShowGoal(true); }}>Edit</Button>
                  {g.status !== "achieved" && <Button variant="outline" size="sm" className="text-2xs h-6" onClick={() => reviewGoal(g, "achieved")}>Mark achieved</Button>}
                  {g.status === "active" && <Button variant="outline" size="sm" className="text-2xs h-6" onClick={() => reviewGoal(g, "paused")}>Pause</Button>}
                  {g.status === "paused" && <Button variant="outline" size="sm" className="text-2xs h-6" onClick={() => reviewGoal(g, "active")}>Resume</Button>}
                  {g.status !== "closed" && <Button variant="outline" size="sm" className="text-2xs h-6" onClick={() => {
                    const r = window.prompt("Reason for closing this goal?");
                    if (r) reviewGoal(g, "closed", r);
                  }}>Close</Button>}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* MEETINGS */}
      {tab === "meetings" && (
        <div className="space-y-2" data-testid="td-meetings-list">
          {is_writer && (
            <Button size="sm" className="text-xs h-7 bg-zinc-950 hover:bg-zinc-800 text-white" onClick={() => setShowNewMeeting(true)} data-testid="td-new-meeting-btn">
              <PlusCircle className="h-3 w-3 mr-1" />Schedule meeting
            </Button>
          )}
          {meetings.length === 0 && <div className="text-2xs text-zinc-500">No meetings scheduled yet.</div>}
          {meetings.map((m) => (
            <Card key={m.id} className="p-3 rounded-md border border-zinc-200 shadow-none" data-testid={`td-meeting-${m.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-sm font-medium">{fmtDateTime(m.scheduled_at)}</div>
                  <div className="text-2xs text-zinc-500">
                    {m.duration_minutes} min · {m.location || "no location"} · focus: {m.focus || "—"}
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <Badge className={`text-2xs ${m.status === "published" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-zinc-100 text-zinc-600 border-zinc-200"}`}>
                    {m.status}
                    {m.published_revision > 0 && ` v${m.published_revision}`}
                  </Badge>
                  {m.published_revision > 0 && (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/mentoring/meetings/${m.id}/export.pdf`}
                      target="_blank" rel="noreferrer"
                      data-testid={`td-meeting-pdf-${m.id}`}
                    >
                      <Button variant="outline" size="sm" className="text-2xs h-6">
                        <FileText className="h-3 w-3 mr-0.5" />PDF
                      </Button>
                    </a>
                  )}
                  <Button variant="outline" size="sm" className="text-2xs h-6" onClick={() => { setActiveMeetingId(m.id); setShowMeeting(true); }} data-testid={`td-open-meeting-${m.id}`}>Open</Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* ACTIONS */}
      {tab === "actions" && (
        <ActionsSection staffId={staffId} isWriter={is_writer} isSelf={is_self} />
      )}

      {/* TRAINING PROGRESS */}
      {tab === "training" && (
        <TrainingProgress staffId={staffId} me={me} canSign={is_writer} />
      )}

      {/* NOTES (Phase 3) */}
      {tab === "notes" && canPrivateNote && (
        <PrivateNotes
          staffId={staffId}
          scopeType="profile"
          scopeId={staffId}
          me={me}
          canWrite={canPrivateNote}
        />
      )}

      {showAudit && (
        <AuditTrailDialog
          open={showAudit}
          onClose={() => setShowAudit(false)}
          staffId={staffId}
          staffName={staff.name}
        />
      )}

      {showGoal && (
        <GoalDialog
          open={showGoal}
          onClose={() => { setShowGoal(false); setEditGoal(null); }}
          onSaved={() => { setShowGoal(false); setEditGoal(null); load(); }}
          staffId={staffId}
          categories={categories}
          goal={editGoal}
        />
      )}
      {showNewMeeting && (
        <NewMeetingDialog
          open={showNewMeeting}
          onClose={() => setShowNewMeeting(false)}
          onCreated={(m) => { setShowNewMeeting(false); setActiveMeetingId(m.id); setShowMeeting(true); load(); }}
          staffId={staffId}
          me={me}
        />
      )}
      {showMeeting && activeMeetingId && (
        <MeetingDialog
          open={showMeeting}
          onClose={() => { setShowMeeting(false); setActiveMeetingId(null); load(); }}
          onSaved={() => load()}
          meetingId={activeMeetingId}
          template={template}
          profile={data}
          me={me}
          isWriter={is_writer}
          isSelf={is_self}
        />
      )}
    </div>
  );
}

function NewMeetingDialog({ open, onClose, onCreated, staffId, me }) {
  const [scheduledAt, setScheduledAt] = useState("");
  const [duration, setDuration] = useState(45);
  const [location, setLocation] = useState("");
  const [focus, setFocus] = useState("");
  const [saving, setSaving] = useState(false);
  async function create() {
    if (!scheduledAt) return toast.error("Pick a date and time");
    setSaving(true);
    try {
      const r = await api.post("/mentoring/meetings", {
        staff_id: staffId,
        scheduled_at: new Date(scheduledAt).toISOString(),
        duration_minutes: duration,
        location, focus,
        mentor_ids: [String(me.id)],
      });
      toast.success("Meeting scheduled");
      onCreated(r.data);
    } catch (e) {
      toast.error("Create failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Schedule mentoring meeting</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <div>
            <Label className="text-2xs">Date and time *</Label>
            <Input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} data-testid="td-meeting-datetime" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-2xs">Duration (min)</Label>
              <Input type="number" min="15" max="180" value={duration} onChange={(e) => setDuration(parseInt(e.target.value) || 45)} data-testid="td-meeting-duration" />
            </div>
            <div>
              <Label className="text-2xs">Location / link</Label>
              <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Clinic room / Zoom" data-testid="td-meeting-location" />
            </div>
          </div>
          <div>
            <Label className="text-2xs">Focus (optional)</Label>
            <Input value={focus} onChange={(e) => setFocus(e.target.value)} data-testid="td-meeting-focus" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={create} disabled={saving} data-testid="td-meeting-create-save">
            {saving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}Schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Actions tab (Phase 2)
// ---------------------------------------------------------------------------
function ActionsSection({ staffId, isWriter, isSelf }) {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [evidenceAction, setEvidenceAction] = useState(null);
  const [reviewEvidence, setReviewEvidence] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/team-development/actions?staff_id=${staffId}`);
      setActions(r.data.actions || []);
    } catch (e) {
      toast.error("Couldn't load actions", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }, [staffId]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-xs text-zinc-500">Loading…</div>;

  return (
    <div className="space-y-2" data-testid="td-actions-list">
      {isWriter && (
        <Button size="sm" className="text-xs h-7 bg-zinc-950 hover:bg-zinc-800 text-white" onClick={() => setShowNew(true)} data-testid="td-new-action-btn">
          <PlusCircle className="h-3 w-3 mr-1" />New action
        </Button>
      )}
      {actions.length === 0 && <div className="text-2xs text-zinc-500">No actions yet.</div>}
      {actions.map((a) => (
        <Card key={a.id} className="p-3 rounded-md border border-zinc-200 shadow-none" data-testid={`td-action-${a.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm font-medium text-zinc-900">{a.description}</div>
              <div className="text-2xs text-zinc-500">
                {a.type}
                {a.due_date && <span className="ml-1">· due {fmtDate(a.due_date)}</span>}
              </div>
              {a.evidence_criteria && (
                <div className="text-2xs mt-1 bg-zinc-50 border border-zinc-100 rounded p-1.5">
                  <span className="text-2xs uppercase tracking-wider text-zinc-500">Evidence criteria</span>
                  <div className="whitespace-pre-wrap">{a.evidence_criteria}</div>
                </div>
              )}
            </div>
            <Badge className={`text-2xs ${a.status === "completed" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : a.status === "submitted" ? "bg-amber-100 text-amber-800 border-amber-200" : a.status === "cancelled" ? "bg-zinc-100 text-zinc-600 border-zinc-200" : "bg-indigo-50 text-indigo-800 border-indigo-200"}`}>
              {a.status}
            </Badge>
          </div>
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            {isSelf && !["completed", "cancelled"].includes(a.status) && (
              <Button variant="outline" size="sm" className="text-2xs h-6" onClick={() => setEvidenceAction(a)} data-testid={`td-action-submit-${a.id}`}>
                <Send className="h-3 w-3 mr-0.5" />Submit evidence
              </Button>
            )}
            {isWriter && a.status === "submitted" && (
              <Button variant="outline" size="sm" className="text-2xs h-6" onClick={async () => {
                const r = await api.get(`/team-development/actions/${a.id}/evidence`);
                const latest = r.data.evidence?.[0];
                if (latest) setReviewEvidence(latest);
                else toast.info("No evidence submitted yet.");
              }} data-testid={`td-action-review-${a.id}`}>
                <CheckCircle2 className="h-3 w-3 mr-0.5" />Review
              </Button>
            )}
          </div>
        </Card>
      ))}

      {showNew && (
        <NewActionDialog
          open={showNew}
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); load(); }}
          staffId={staffId}
        />
      )}
      {evidenceAction && (
        <SubmitEvidenceDialog
          action={evidenceAction}
          onClose={() => setEvidenceAction(null)}
          onSubmitted={() => { setEvidenceAction(null); load(); }}
        />
      )}
      {reviewEvidence && (
        <ReviewEvidenceDialog
          evidence={reviewEvidence}
          onClose={() => setReviewEvidence(null)}
          onReviewed={() => { setReviewEvidence(null); load(); }}
        />
      )}
    </div>
  );
}

function NewActionDialog({ open, onClose, onCreated, staffId }) {
  const [desc, setDesc] = useState("");
  const [type, setType] = useState("practice");
  const [criteria, setCriteria] = useState("");
  const [due, setDue] = useState("");
  const [saving, setSaving] = useState(false);
  async function save() {
    if (!desc.trim()) return toast.error("Description required");
    setSaving(true);
    try {
      await api.post("/team-development/actions", {
        staff_id: staffId, description: desc, type, evidence_criteria: criteria, due_date: due || null,
      });
      toast.success("Action created");
      onCreated();
    } catch (e) {
      toast.error("Create failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>New development action</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <div>
            <Label className="text-2xs">Description *</Label>
            <Textarea rows={2} value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="td-action-desc" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-2xs">Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="td-action-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="practice">Practice</SelectItem>
                  <SelectItem value="observation">Observation</SelectItem>
                  <SelectItem value="learning">Learning</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-2xs">Due date</Label>
              <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="td-action-due" />
            </div>
          </div>
          <div>
            <Label className="text-2xs">Evidence criteria</Label>
            <Textarea rows={2} value={criteria} onChange={(e) => setCriteria(e.target.value)} placeholder="How will we know this is done?" data-testid="td-action-criteria" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="td-action-save">{saving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SubmitEvidenceDialog({ action, onClose, onSubmitted }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  async function submit() {
    if (!text.trim()) return toast.error("Add some evidence text");
    setSaving(true);
    try {
      await api.post(`/team-development/actions/${action.id}/evidence`, { text });
      toast.success("Evidence submitted for review");
      onSubmitted();
    } catch (e) {
      toast.error("Submit failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Submit evidence</DialogTitle>
          <DialogDescription className="text-2xs">{action.description}</DialogDescription>
        </DialogHeader>
        <div>
          <Label className="text-2xs">Evidence</Label>
          <Textarea rows={5} value={text} onChange={(e) => setText(e.target.value)} data-testid="td-evidence-text" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="td-evidence-submit">{saving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}Submit</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReviewEvidenceDialog({ evidence, onClose, onReviewed }) {
  const [feedback, setFeedback] = useState("");
  const [saving, setSaving] = useState(false);
  async function act(outcome) {
    if (outcome === "returned" && !feedback.trim()) return toast.error("Feedback required to return");
    setSaving(true);
    try {
      await api.post(`/team-development/evidence/${evidence.id}/review`, { outcome, feedback });
      toast.success(outcome === "approved" ? "Approved — action completed" : "Returned to staff member");
      onReviewed();
    } catch (e) {
      toast.error("Review failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Review evidence</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <div className="bg-zinc-50 border border-zinc-100 rounded p-2 text-2xs whitespace-pre-wrap">
            {evidence.text || "(no text)"}
          </div>
          <div className="text-2xs text-zinc-500">Submitted {fmtDateTime(evidence.submitted_at)}</div>
          <div>
            <Label className="text-2xs">Feedback (required if returning)</Label>
            <Textarea rows={3} value={feedback} onChange={(e) => setFeedback(e.target.value)} data-testid="td-evidence-feedback" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => act("returned")} disabled={saving} data-testid="td-evidence-return">Return</Button>
          <Button onClick={() => act("approved")} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="td-evidence-approve">
            {saving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}Approve · Complete action
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Root page
// ---------------------------------------------------------------------------
export default function TeamDevelopment() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [ref, setRef] = useState(null);
  const [pickedId, setPickedId] = useState(params.get("staff") || null);
  const [staffList, setStaffList] = useState([]);
  const [mentorFor, setMentorFor] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/team-development/categories");
        setRef(r.data);
      } catch (e) {
        // Non-fatal — categories just populate the goal picker
      }
    })();
  }, []);

  function pick(staff) {
    setPickedId(staff.staff_id);
    setParams({ staff: staff.staff_id });
  }

  const canManageMentors = ["owner", "manager"].includes((user?.role || "").toLowerCase());

  return (
    <div className="max-w-[1300px] mx-auto p-4 space-y-3" data-testid="team-development-page">
      <div className="flex items-center gap-2">
        <Link to="/learning" className="text-zinc-400 hover:text-zinc-700" data-testid="td-back-learning">
          <ChevronLeft className="h-5 w-5" />
        </Link>
        <h1 className="text-2xl font-semibold text-zinc-950 flex items-center gap-2">
          <Users className="h-6 w-6" /> Team Development
        </h1>
      </div>
      <div className="text-xs text-zinc-500">
        Mentoring meetings, development goals, private manager notes and the full audit trail.
      </div>
      {!pickedId && (
        <TeamList
          key={refreshTick}
          onPick={pick}
          me={user}
          onStaffLoaded={setStaffList}
          onManageMentors={(s) => setMentorFor(s)}
        />
      )}
      {pickedId && ref && (
        <StaffProfile
          staffId={pickedId}
          me={user}
          categories={ref.categories}
          template={ref.template}
          onBack={() => { setPickedId(null); setParams({}); }}
        />
      )}
      {mentorFor && canManageMentors && (
        <MentorAssignmentDialog
          open={!!mentorFor}
          onClose={() => setMentorFor(null)}
          staff={mentorFor}
          candidates={staffList}
          onChange={() => setRefreshTick((t) => t + 1)}
        />
      )}
    </div>
  );
}
